import * as XLSX from "xlsx";
import adviserMap from "./quilterAdviserMap.json";
import paymentTypeMap from "./quilterPaymentTypeMap.json";
import { mapFinplanPlanType } from "./finplanPlanTypes";

export const QUILTER_TABLE_HEADERS = ["Index", "Recipient", "LinkedAdviser", "PaymentCreated", "PolicyAdviser", "ClientName", "PolicyAccountNumber", "PolicyProvider", "BusinessType", "CommissionType", "PaymentAmount", "GrossReceived"];

const makeUniqueHeaders = (headerRow) => {
  const counts = new Map();
  return headerRow.map((value, index) => {
    const base = String(value ?? "").trim() || `Column ${index + 1}`;
    const count = (counts.get(base) || 0) + 1;
    counts.set(base, count);
    return count === 1 ? base : `${base} (${count})`;
  });
};

const joinValues = (...values) => values.map((value) => String(value ?? "").trim()).filter(Boolean).join(" - ");

const formatPaidDate = (value) => {
  const text = String(value ?? "").trim();
  const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (!match) return text;
  const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
  return new Date(year, Number(match[2]) - 1, Number(match[1])).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

export const normaliseQuilterRows = (rows = []) => rows.map((row, index) => {
  const sourceAdviserName = String(row["Account Name"] ?? row.SourceAdviserName ?? row.PolicyAdviser ?? "").trim();
  const mappedAdviserName = adviserMap[sourceAdviserName] || row.MappedAdviserName || sourceAdviserName;
  const product = String(
    row.QuilterPlanType ?? row.Product ?? row.BusinessType ?? row.Type ?? "",
  ).trim();
  const revenueType = String(row["Main Revenue Type"] ?? "").trim();
  const basis = String(row.Basis ?? "").trim();
  return {
    Index: String(index + 1),
    Recipient: String(row["Adviser Name"] ?? row.Recipient ?? "").trim(),
    LinkedAdviser: String(row.LinkedAdviser ?? "").trim(),
    SourceAdviserName: sourceAdviserName,
    MappedAdviserName: mappedAdviserName,
    PaymentCreated: row["Paid Date"] !== undefined ? formatPaidDate(row["Paid Date"]) : String(row.PaymentCreated ?? "").trim(),
    PolicyAdviser: mappedAdviserName,
    ClientName: String(row.Client ?? row.ClientName ?? "").trim(),
    PolicyAccountNumber: String(row.PolicyNo ?? row.PolicyAccountNumber ?? "").trim(),
    PolicyProvider: String(row.Supplier ?? row.PolicyProvider ?? "").trim(),
    BusinessType: mapFinplanPlanType(product),
    QuilterPlanType: product,
    CommissionType: revenueType || basis
      ? joinValues(paymentTypeMap[revenueType] || revenueType, paymentTypeMap[basis] || basis)
      : String(row.CommissionType ?? "").trim(),
    PaymentAmount: String(row["Payment Amount"] ?? row.PaymentAmount ?? "").trim(),
    GrossReceived: String(row.Gross ?? row.GrossReceived ?? "").trim(),
  };
});

export const matrixToQuilterStatement = (matrix = []) => {
  const nonEmptyRows = matrix.filter((row) => Array.isArray(row) && row.some((cell) => String(cell ?? "").trim() !== ""));
  if (nonEmptyRows.length < 2) throw new Error("The selected file does not contain any statement entries.");
  const sourceHeaders = makeUniqueHeaders(nonEmptyRows[0]);
  const sourceRows = nonEmptyRows.slice(1).map((row) => sourceHeaders.reduce((entry, header, index) => {
    entry[header] = String(row[index] ?? "").trim();
    return entry;
  }, {}));
  return { sourceHeaders, headers: QUILTER_TABLE_HEADERS, rows: normaliseQuilterRows(sourceRows) };
};

export const parseQuilterStatementFile = async (file) => {
  if (!file) throw new Error("Select a statement to upload.");
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (!["csv", "xlsx", "xls"].includes(extension)) throw new Error("Upload a CSV or Excel file.");
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: false });
  const firstSheet = workbook.SheetNames[0];
  if (!firstSheet) throw new Error("The selected workbook does not contain a sheet.");
  const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], { header: 1, defval: "", raw: false, dateNF: "dd/mm/yyyy" });
  return matrixToQuilterStatement(matrix);
};
